import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import MenuIcon from "@mui/icons-material/Menu";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material";
import DsenseLogoUrl from "../assests/icons/prosm-logo.svg";
import PrismLogoIcon from "../components/PrismLogoIcon";
import ThinkingStages from "../components/ThinkingStages";
import VisualSummary from "../components/VisualSummary";
import {
  buildTableChartConfig,
  buildEchartsOption,
  CHART_TYPE_LABELS,
} from "../utils/chartBuilder";
import { marked } from "marked";
import * as echarts from "echarts";

// Configure marked options
marked.setOptions({
  gfm: true,
  breaks: true,
  sanitize: true,
});

// Add CSS styles for markdown content
const markdownStyles = `
  .markdown-content h1 {
    margin-bottom: 8px !important;
    color: #1a1a2e !important;
    font-weight: 700 !important;
    font-size: 1.25rem !important;
    margin-top: 16px !important;
  }
  .markdown-content h2 {
    margin-bottom: 8px !important;
    color: #1a1a2e !important;
    font-weight: 600 !important;
    font-size: 1.1rem !important;
    margin-top: 20px !important;
    padding-top: 12px !important;
  }
  .markdown-content h3 {
    margin-bottom: 6px !important;
    color: #1a1a2e !important;
    font-weight: 600 !important;
    font-size: 1rem !important;
    margin-top: 10px !important;
  }
  .markdown-content p {
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1.6 !important;
    color: #374151 !important;
    margin-bottom: 8px !important;
  }
  .markdown-content ul, .markdown-content ol {
    padding-left: 20px !important;
    margin-top: 8px !important;
    margin-bottom: 8px !important;
  }
  .markdown-content li {
    margin-bottom: 4px !important;
    color: #374151 !important;
    line-height: 1.6 !important;
  }
  .markdown-content a {
    color: #2563eb !important;
    text-decoration: underline !important;
  }
  .markdown-content blockquote {
    border-left: 4px solid #e5e7eb !important;
    margin: 8px 0 !important;
    padding: 8px 12px !important;
    background: #f9fafb !important;
    color: #4b5563 !important;
  }
  .markdown-content code {
    background: #f3f4f6 !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    font-size: 0.85em !important;
    color: #1f2937 !important;
  }
  .markdown-content pre {
    background: #1e1e1e !important;
    color: #d4d4d4 !important;
    padding: 12px !important;
    border-radius: 8px !important;
    overflow-x: auto !important;
    margin: 8px 0 !important;
  }
  .markdown-content pre code {
    background: transparent !important;
    padding: 0 !important;
    color: inherit !important;
  }
  .markdown-content img {
    max-width: 100% !important;
    height: auto !important;
    display: block !important;
    margin: 8px 0 !important;
    border-radius: 8px !important;
    cursor: zoom-in !important;
  }
  .markdown-content table {
    width: max-content !important;
    min-width: 100% !important;
    border-collapse: collapse !important;
    margin-top: 8px !important;
    margin-bottom: 8px !important;
    font-size: 0.875rem !important;
  }
  .markdown-content th {
    text-align: left !important;
    border: 1px solid #e5e7eb !important;
    padding: 8px 12px !important;
    background: #f9fafb !important;
    font-weight: 600 !important;
    color: #1f2937 !important;
  }
  .markdown-content td {
    border: 1px solid #f3f4f6 !important;
    padding: 8px 12px !important;
    color: #374151 !important;
  }
  .markdown-content.error-message p {
    color: #dc2626 !important;
  }
`;

// Helper to safely escape plain text to HTML
const escapeHtml = (str) => {
  if (str == null) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

// Normalize some common markdown issues that can happen with streaming,
// e.g. missing newlines before heading hashes like "text### Heading".
const normalizeMarkdownSyntax = (text) => {
  if (text == null) return "";
  let s = String(text);

  // 1) Ensure there is a newline before a heading marker if it's stuck to text.
  // e.g. "Competitive Landscape### 6.1" -> "Competitive Landscape\n### 6.1".
  s = s.replace(/([^\n])([ \t]*)#{2,6}\s/g, (m, before) => {
    return `${before}\n${m.slice(before.length)}`;
  });

  // 2) Auto-insert a markdown table separator line when we see consecutive lines
  // with '|' but no explicit header separator (common when the model forgets
  // the --- row). This helps both visual tables and chart extraction.
  const lines = s.split("\n");
  const out = [];

  const isSeparatorLine = (line) => {
    if (!line || !line.includes("|")) return false;
    const trimmed = line.trim();
    if (!trimmed.includes("-")) return false;
    // When removing pipes and spaces, should mostly be dashes
    const core = trimmed.replace(/[\s|:]/g, "");
    return core.length > 0 && /^-+$/.test(core);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    out.push(line);

    // Check for potential header row followed by another '|' row but no
    // separator in between. Only do this when the previous line is NOT
    // already part of the same table block, to avoid injecting extra
    // separators inside the body.
    if (
      line &&
      line.includes("|") &&
      !isSeparatorLine(line) &&
      (i === 0 || !lines[i - 1] || !lines[i - 1].includes("|")) &&
      i + 1 < lines.length &&
      lines[i + 1] &&
      lines[i + 1].includes("|") &&
      !isSeparatorLine(lines[i + 1])
    ) {
      // Insert a synthetic separator line based on the number of columns.
      const parts = line.split("|");
      // Ignore empty cells from leading/trailing pipes when counting columns
      const colCount = parts.filter((p) => p.trim().length > 0).length || 1;
      const cells = Array(colCount).fill(" --- ");
      const sep = `|${cells.join("|")}|`;
      out.push(sep);
    }
  }

  return out.join("\n");
};

// Helper function to render markdown to HTML (robust against bad input)
const renderMarkdown = (text) => {
  try {
    const normalized = normalizeMarkdownSyntax(
      (text == null ? "" : String(text)).replace(/\r\n/g, "\n")
    );
    return { __html: marked.parse(normalized) };
  } catch (err) {
    console.error("Markdown render failed, falling back to plain text", err);
    return { __html: `<pre>${escapeHtml(text)}</pre>` };
  }
};

// Convert any [path] occurrence (file:///... or absolute /... ) into a Markdown link [link](path)
const transformPathsToLinks = (text) => {
  if (!text) return "";
  const pattern = /\[((?:file:\/\/\/|\/)[^\]\s]+)\]/g;
  return text.replace(pattern, (match, p1) => {
    const href = p1.startsWith("/") ? `file://${p1}` : p1;
    return `[link](${href})`;
  });
};

// Extract "next question" suggestions from the end of a bot response.
// Returns { cleanText, suggestions[] }
const extractSuggestions = (text) => {
  if (!text) return { cleanText: text, suggestions: [] };

  // Match a trailing section that starts with a line like
  // "What would you like to explore next?" or "Follow-up Questions:"
  // (or similar phrasing) followed by numbered/bulleted questions or plain
  // lines ending with "?". Allows an optional ':' or '.' after the header
  // text and tolerates surrounding bold (`**…**`) / heading (`#`) markers.
  const triggerPattern =
    /\n*(#{0,3}\s*\**(?:What would you like to (?:explore|know|learn|ask|investigate)[^?\n]*\??|(?:\d+\s+)?Follow[- ]?up Questions?)[:.]?\**\s*[:.]?\s*)\n+([\s\S]+)$/i;
  const match = text.match(triggerPattern);
  if (!match) return { cleanText: text, suggestions: [] };

  const headerLine = match[1];
  const body = match[2];

  // Extract individual suggestion lines (numbered, bulleted, or plain lines ending with ?)
  const lines = body
    .split("\n")
    .map((l) => l.replace(/^[\s]*[-*•\d.)\]]+[\s.)\]]*/, "").trim())
    .filter((l) => l.length > 15);

  if (lines.length === 0) return { cleanText: text, suggestions: [] };

  // Remove the suggestions section from the original text
  const cleanText = text.slice(0, text.indexOf(headerLine)).trimEnd();
  return { cleanText, suggestions: lines };
};


// Standalone TableChart component so React does not remount it on each
// Homepage re-render. It owns its ECharts instance and only updates when
// tableMarkdown or chartType change.
const TableChart = React.memo(function TableChart({ tableMarkdown, chartType }) {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // Initialize chart once
  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);

      const handleResize = () => {
        chartInstanceRef.current && chartInstanceRef.current.resize();
      };
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("resize", handleResize);
        if (chartInstanceRef.current) {
          chartInstanceRef.current.dispose();
          chartInstanceRef.current = null;
        }
      };
    }
  }, []);

  // Update options when data or type changes
  useEffect(() => {
    const config = buildTableChartConfig(tableMarkdown);
    if (!config || !chartInstanceRef.current) return;

    const resolvedType = chartType || config.bestType || "bar";
    const option = buildEchartsOption(config, resolvedType);
    if (option) {
      chartInstanceRef.current.setOption(option, true);
    }
  }, [tableMarkdown, chartType]);

  return <div style={{ width: "100%", height: 260 }} ref={chartRef} />;
});

const MessageBubble = styled(Box)(({ theme }) => ({
  padding: "10px 16px",
  borderRadius: "20px",
  backgroundColor: "#f4f4f4",
  color: "#1a1a1a",
  fontSize: "0.9375rem",
  lineHeight: 1.6,
  maxWidth: "85%",
  [theme.breakpoints.up("sm")]: {
    maxWidth: "70%",
  },
  wordBreak: "break-word",
  opacity: 0,
  transform: "translateY(8px)",
  animation: "fadeSlideIn 0.3s ease-out forwards",
  "@keyframes fadeSlideIn": {
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

const ChatContainer = styled(Box)(() => ({
  flexGrow: 1,
  overflowY: "auto",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#ffffff",
  scrollBehavior: "smooth",
  minHeight: 0,
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: "8px 12px 12px",
  [theme.breakpoints.up("sm")]: {
    padding: "12px 16px 20px",
  },
  display: "flex",
  justifyContent: "center",
  backgroundColor: "#ffffff",
}));

const AnimatedSendIcon = styled(SendIcon, {
  shouldForwardProp: (prop) => prop !== "canSend",
})(({ canSend }) => ({
  transition: "all 0.2s ease",
  fontSize: "18px",
  color: canSend ? "#ffffff" : "#a1a1aa",
}));


const AnswerActions = React.memo(function AnswerActions({ text }) {
  const [copied, setCopied] = React.useState(false);

  const getPlainText = () => {
    const tmp = document.createElement("div");
    tmp.innerHTML = marked.parse(String(text || ""));
    return tmp.textContent || tmp.innerText || text;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async () => {
    const [{ jsPDF }, html2canvas, { default: JSZip }] = await Promise.all([
      import("jspdf"),
      import("html2canvas").then((m) => m.default || m),
      import("jszip"),
    ]);

    const getWatermark = async () => {
      try {
        const res = await fetch("/GVR_Watermark.docx");
        if (!res.ok) {
          console.warn("Watermark docx not found at /GVR_Watermark.docx");
          return null;
        }
        const buf = await res.arrayBuffer();
        const zip = await JSZip.loadAsync(buf);
        const imgFile = zip.file("word/media/image1.png");
        if (!imgFile) {
          console.warn("Watermark image not found in docx at word/media/image1.png");
          return null;
        }
        const base64 = await imgFile.async("base64");
        const dataUrl = `data:image/png;base64,${base64}`;

        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });

        return { dataUrl, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height };
      } catch {
        return null;
      }
    };

    const watermark = await getWatermark();

    const htmlContent = marked.parse(String(text || ""));

    // Use a hidden iframe so html2canvas cannot affect the main document layout
    const iframe = document.createElement("iframe");
    iframe.style.cssText =
      "position:fixed;left:-10000px;top:-10000px;width:600px;height:1px;" +
      "border:none;visibility:hidden;pointer-events:none;";
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write("<!DOCTYPE html><html><head></head><body style=\"margin:0;padding:0;\"></body></html>");
    iframeDoc.close();

    const container = iframeDoc.createElement("div");
    container.style.cssText =
      "width:550px;padding:24px;" +
      "font-family:Helvetica,Arial,sans-serif;font-size:12px;line-height:1.7;" +
      "color:#1a1a1a;background:#fff;";
    container.innerHTML = `<style>
      *{box-sizing:border-box}
      h1{font-size:18px;font-weight:700;margin:14px 0 8px;color:#1a1a2e}
      h2{font-size:15px;font-weight:600;margin:12px 0 6px;color:#1a1a2e}
      h3{font-size:13px;font-weight:600;margin:10px 0 5px;color:#1a1a2e}
      p{margin:0 0 8px;color:#374151}
      ul,ol{padding-left:20px;margin:6px 0 8px}
      li{margin-bottom:3px;color:#374151}
      table{width:100%;border-collapse:collapse;margin:8px 0;font-size:11px}
      th{text-align:left;border:1px solid #d1d5db;padding:5px 8px;background:#f3f4f6;font-weight:600}
      td{border:1px solid #e5e7eb;padding:5px 8px}
      code{background:#f3f4f6;padding:1px 4px;border-radius:3px;font-size:11px}
      pre{background:#1e1e1e;color:#d4d4d4;padding:10px;border-radius:6px;font-size:11px;margin:8px 0;white-space:pre-wrap;word-break:break-word}
      pre code{background:transparent;padding:0;color:inherit}
      blockquote{border-left:3px solid #d1d5db;margin:8px 0;padding:6px 10px;background:#f9fafb;color:#4b5563}
      a{color:#2563eb}
      img{max-width:100%;height:auto}
    </style>${htmlContent}`;
    iframeDoc.body.appendChild(container);

    // Resize iframe to fit content so html2canvas can capture it fully
    iframe.style.height = container.scrollHeight + 50 + "px";

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        windowWidth: 600,
        windowHeight: container.scrollHeight + 50,
      });

      // A4 dimensions in pt
      const A4_W = 595.28;
      const A4_H = 841.89;
      const MARGIN_TOP = 50;
      const MARGIN_BOTTOM = 50;
      const MARGIN_X = 40;
      const contentWidth = A4_W - MARGIN_X * 2;
      const contentHeight = A4_H - MARGIN_TOP - MARGIN_BOTTOM;

      // Scale factor: canvas pixels → PDF pt
      const scale = contentWidth / canvas.width;
      const totalPdfHeight = canvas.height * scale;

      // If it fits on one page, simple case
      if (totalPdfHeight <= contentHeight) {
        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const imgData = canvas.toDataURL("image/png");
        doc.addImage(imgData, "PNG", MARGIN_X, MARGIN_TOP, contentWidth, totalPdfHeight);

        if (watermark?.dataUrl && watermark.width && watermark.height) {
          const aspect = watermark.height / watermark.width;
          const wmW = A4_W * 0.85;
          const wmH = wmW * aspect;
          const wmX = (A4_W - wmW) / 2;
          const wmY = (A4_H - wmH) / 2;
          try {
            if (doc.GState) doc.setGState(new doc.GState({ opacity: 0.18 }));
            doc.addImage(watermark.dataUrl, "PNG", wmX, wmY, wmW, wmH);
            if (doc.GState) doc.setGState(new doc.GState({ opacity: 1 }));
          } catch {
            /* ignore watermark failures */
          }
        }

        doc.setFontSize(8);
        doc.setTextColor(160);
        doc.text("Page 1 of 1", A4_W / 2, A4_H - 25, { align: "center" });
        doc.save(`response-${Date.now()}.pdf`);
        return;
      }

      // Multi-page: slice the canvas at clean break points
      const sliceHeightPx = Math.floor(contentHeight / scale); // max px per page
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      const slices = [];
      let y = 0;

      while (y < canvas.height) {
        let cutY = Math.min(y + sliceHeightPx, canvas.height);

        // If not the last slice, scan upward for a clean break (white row)
        if (cutY < canvas.height) {
          const scanRange = Math.min(Math.floor(sliceHeightPx * 0.15), 80);
          let bestCut = cutY;
          for (let row = cutY; row > cutY - scanRange && row > y; row--) {
            const rowData = ctx.getImageData(0, row, canvas.width, 1).data;
            let isWhite = true;
            for (let px = 0; px < rowData.length; px += 16) {
              if (rowData[px] < 240 || rowData[px + 1] < 240 || rowData[px + 2] < 240) {
                isWhite = false;
                break;
              }
            }
            if (isWhite) {
              bestCut = row;
              break;
            }
          }
          cutY = bestCut;
        }

        const sliceH = cutY - y;
        if (sliceH <= 0) break;

        // Extract this slice to its own canvas
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceH;
        const sliceCtx = sliceCanvas.getContext("2d");
        sliceCtx.drawImage(canvas, 0, y, canvas.width, sliceH, 0, 0, canvas.width, sliceH);
        slices.push({ data: sliceCanvas.toDataURL("image/png"), h: sliceH });

        y = cutY;
      }

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const totalPages = slices.length;

      slices.forEach((slice, i) => {
        if (i > 0) doc.addPage();

        const slicePdfH = slice.h * scale;
        doc.addImage(slice.data, "PNG", MARGIN_X, MARGIN_TOP, contentWidth, slicePdfH);

        if (watermark?.dataUrl && watermark.width && watermark.height) {
          const aspect = watermark.height / watermark.width;
          const wmW = A4_W * 0.85;
          const wmH = wmW * aspect;
          const wmX = (A4_W - wmW) / 2;
          const wmY = (A4_H - wmH) / 2;
          try {
            if (doc.GState) doc.setGState(new doc.GState({ opacity: 0.18 }));
            doc.addImage(watermark.dataUrl, "PNG", wmX, wmY, wmW, wmH);
            if (doc.GState) doc.setGState(new doc.GState({ opacity: 1 }));
          } catch {
            /* ignore watermark failures */
          }
        }

        // Footer: page number
        doc.setFontSize(8);
        doc.setTextColor(160);
        doc.text(`Page ${i + 1} of ${totalPages}`, A4_W / 2, A4_H - 25, { align: "center" });
      });

      doc.save(`response-${Date.now()}.pdf`);
    } finally {
      document.body.removeChild(iframe);
    }
  };

  const btnStyle = {
    p: 0.25,
    transition: "opacity 0.15s, background-color 0.15s",
    "&:hover": { backgroundColor: "#f4f4f5" },
    "&.Mui-focusVisible": { backgroundColor: "transparent" },
    "&:focus": { outline: "none" },
    "&:active": { backgroundColor: "transparent" },
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.25,
        justifyContent: "flex-end",
        mb: 0,
      }}
    >
      <IconButton
        size="small"
        disableRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleCopy}
        sx={{ ...btnStyle, color: copied ? "#22c55e" : "#9ca3af" }}
        title={copied ? "Copied!" : "Copy"}
      >
        {copied ? (
          <CheckIcon sx={{ fontSize: 16 }} />
        ) : (
          <ContentCopyIcon sx={{ fontSize: 16 }} />
        )}
      </IconButton>
      <IconButton
        size="small"
        disableRipple
        disableFocusRipple
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleDownload}
        sx={{ ...btnStyle, color: "#9ca3af" }}
        title="Download"
      >
        <DownloadIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
});

// Fullscreen modal for viewing wide tables
const TableModal = React.memo(function TableModal({ html, onClose }) {
  React.useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    // Prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1, sm: 3 },
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "relative",
          backgroundColor: "#ffffff",
          borderRadius: { xs: "8px", sm: "12px" },
          width: { xs: "calc(100vw - 16px)", sm: "calc(100vw - 48px)" },
          maxHeight: { xs: "calc(100vh - 16px)", sm: "calc(100vh - 48px)" },
          overflow: "auto",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 2,
            width: 28,
            height: 28,
            color: "#6b7280",
            backgroundColor: "#f4f4f5",
            "&:hover": { backgroundColor: "#e5e7eb" },
          }}
        >
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
        <Box
          dangerouslySetInnerHTML={{ __html: html }}
          className="markdown-content"
          sx={{
            fontSize: "0.9375rem",
            overflowX: "auto",
            "& table": {
              width: "max-content !important",
              minWidth: "100% !important",
              tableLayout: "auto !important",
              "& th, & td": {
                whiteSpace: "nowrap",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
});

// Wrapper for table sections — detects horizontal overflow and shows expand button
const ExpandableTable = React.memo(function ExpandableTable({
  content,
  onClick: handleMarkdownClick,
}) {
  const containerRef = React.useRef(null);
  const [overflows, setOverflows] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);

  const html = React.useMemo(
    () => renderMarkdown(transformPathsToLinks(content)),
    [content]
  );

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [content]);

  const handleClose = React.useCallback(() => setModalOpen(false), []);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          "&:hover .expand-overlay": { opacity: 1 },
          "&:hover .expand-btn": { opacity: 1 },
        }}
      >
        <div
          ref={containerRef}
          style={{
            fontSize: "0.9375rem",
            overflowX: "auto",
            width: "100%",
            textAlign: "left",
          }}
          dangerouslySetInnerHTML={html}
          onClick={handleMarkdownClick}
          className="markdown-content"
        />
        {overflows && (
          <>
            {/* Transparent overlay on hover */}
            <Box
              className="expand-overlay"
              onClick={() => setModalOpen(true)}
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0,
                transition: "opacity 0.2s",
                backgroundColor: "rgba(255,255,255,0.55)",
                backdropFilter: "blur(1px)",
                borderRadius: "8px",
                cursor: "pointer",
                zIndex: 1,
                "@media (hover: none)": { opacity: 0.6 },
              }}
            />
            {/* Expand button */}
            <Box
              className="expand-btn"
              onClick={() => setModalOpen(true)}
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                opacity: 0,
                transition: "opacity 0.2s",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 2,
                py: 1,
                borderRadius: "10px",
                backgroundColor: "rgba(255,255,255,0.95)",
                boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "#374151",
                fontFamily: "inherit",
                zIndex: 2,
                "&:hover": { backgroundColor: "#ffffff", boxShadow: "0 4px 16px rgba(0,0,0,0.2)" },
                "@media (hover: none)": { opacity: 0.9 },
              }}
            >
              <OpenInFullIcon sx={{ fontSize: 16 }} />
              Expand Table
            </Box>
          </>
        )}
      </Box>
      {modalOpen && <TableModal html={html.__html} onClose={handleClose} />}
    </>
  );
});

const generateId = () => {
  const c = typeof crypto !== "undefined" ? crypto : null;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const Homepage = () => {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const currentBotIdRef = useRef(null);
  const typingBufferRef = useRef("");
  const displayedTextRef = useRef("");
  const typingIntervalRef = useRef(null);
  const lastFailedMessageRef = useRef(null);
  const abortControllerRef = useRef(null);
  const pendingActionRef = useRef(null);
  const [showAbortDialog, setShowAbortDialog] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  // Per-table chart state keyed by `${messageIndex}-${sectionIndex}`
  const [tableChartsState, setTableChartsState] = useState({});

  const [sessionId, setSessionId] = useState(generateId);

  // Chat history: up to N sessions persisted in localStorage
  const [sessions, setSessions] = useState(() => {
    try {
      const stored = localStorage.getItem("dsense_chat_sessions");
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      // Clean up stale streaming flags from interrupted sessions
      return parsed.map((s) => ({
        ...s,
        messages: (s.messages || []).map((m) =>
          m.streaming ? { ...m, streaming: false } : m
        ),
      }));
    } catch {
      return [];
    }
  });
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Refs for accessing latest values in callbacks without stale closures
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const tableChartsStateRef = useRef(tableChartsState);
  tableChartsStateRef.current = tableChartsState;
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  const API_ENDPOINT =
    (window._env_ && window._env_.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL;
  const MAX_HISTORY_QUESTIONS = Number.parseInt(
    (window._env_ && window._env_.REACT_APP_MAX_HISTORY_QUESTIONS) ||
      process.env.REACT_APP_MAX_HISTORY_QUESTIONS ||
      "5",
    10
  );
  const MAX_SESSIONS = Number.parseInt(
    (window._env_ && window._env_.REACT_APP_MAX_SESSIONS) ||
      process.env.REACT_APP_MAX_SESSIONS ||
      "10",
    10
  );
  const STREAM_TIMEOUT_MS = Number.parseInt(
    (window._env_ && window._env_.REACT_APP_STREAM_TIMEOUT_MS) ||
      process.env.REACT_APP_STREAM_TIMEOUT_MS ||
      "120000",
    10
  );

  const lastScrollTimeRef = useRef(0);

  const scrollToBottom = useCallback((force) => {
    const now = Date.now();
    // Throttle scroll calls to every 200ms unless forced
    if (!force && now - lastScrollTimeRef.current < 200) return;
    lastScrollTimeRef.current = now;

    requestAnimationFrame(() => {
      const container = chatContainerRef.current;
      if (!container) return;
      // Only auto-scroll if user is near the bottom (within 150px)
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom < 150) {
        // Direct scrollTop assignment — no competing smooth scroll animations
        container.scrollTop = container.scrollHeight;
      }
    });
  }, []);

  const resetStreamingState = useCallback(() => {
    setIsLoading(false);
    setInput("");
    currentBotIdRef.current = null;
    typingBufferRef.current = "";
    displayedTextRef.current = "";
    if (typingIntervalRef.current) {
      cancelAnimationFrame(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
  }, []);

  const isStreamingActive = useCallback(() =>
    isLoading || currentBotIdRef.current !== null || messagesRef.current.some((m) => m.streaming), [isLoading]);

  const abortCurrentStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (typingIntervalRef.current) {
      cancelAnimationFrame(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    // Remove the streaming bot response and the user message that triggered it
    setMessages((prev) => {
      const streamIdx = prev.findIndex((msg) => msg.streaming);
      if (streamIdx === -1) return prev;
      // The user message is the one right before the streaming bot message
      const userIdx = streamIdx > 0 && prev[streamIdx - 1].sender === "user" ? streamIdx - 1 : -1;
      return prev.filter((_, i) => i !== streamIdx && i !== userIdx);
    });
    resetStreamingState();
  };

  const handleAbortDialogContinue = () => {
    pendingActionRef.current = null;
    setShowAbortDialog(false);
  };

  const handleAbortDialogAbort = () => {
    abortCurrentStream();
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setShowAbortDialog(false);
    if (action) action();
  };

  const handleNewChat = () => {
    const performNewChat = () => {
      // Save current session if it has messages
      if (activeSessionId && messagesRef.current.length > 0) {
        setSessions((prev) =>
          prev.map((s) =>
            s.id === activeSessionId
              ? { ...s, messages: messagesRef.current, tableChartsState: tableChartsStateRef.current, updatedAt: Date.now() }
              : s
          )
        );
      }

      setMessages([]);
      setTableChartsState({});
      resetStreamingState();
      setSessionId(generateId());
      setActiveSessionId(null);
      navigate("/", { replace: false });
      // Auto-close sidebar on mobile
      if (window.innerWidth <= 768) setSidebarOpen(false);
    };

    if (isStreamingActive()) {
      pendingActionRef.current = performNewChat;
      setShowAbortDialog(true);
      return;
    }

    performNewChat();
  };

  const deleteSession = useCallback(
    (targetId, e) => {
      if (e) e.stopPropagation();
      setSessions((prev) => prev.filter((s) => s.id !== targetId));
      if (targetId === activeSessionId) {
        setMessages([]);
        setTableChartsState({});
        setSessionId(generateId());
        setActiveSessionId(null);
        resetStreamingState();
        navigate("/", { replace: true });
      }
      if (window.innerWidth <= 768) setSidebarOpen(false);
    },
    [activeSessionId, resetStreamingState, navigate]
  );

  const switchSession = useCallback(
    (targetId) => {
      if (targetId === activeSessionId) return;

      const performSwitch = () => {
        // Save current session
        if (activeSessionId && messagesRef.current.length > 0) {
          setSessions((prev) =>
            prev.map((s) =>
              s.id === activeSessionId
                ? { ...s, messages: messagesRef.current, tableChartsState: tableChartsStateRef.current, updatedAt: Date.now() }
                : s
            )
          );
        }

        // Load target session directly from the ref (always latest)
        const target = sessionsRef.current.find((s) => s.id === targetId);
        if (!target) return;

        // Ensure loaded messages have no stale streaming flags
        const cleanMessages = (target.messages || []).map((m) =>
          m.streaming ? { ...m, streaming: false } : m
        );
        setMessages(cleanMessages);
        setTableChartsState(target.tableChartsState || {});
        setSessionId(target.sessionId);
        setActiveSessionId(targetId);
        resetStreamingState();
        navigate(`/${targetId}`, { replace: false });
        if (window.innerWidth <= 768) setSidebarOpen(false);
      };

      if (isStreamingActive()) {
        pendingActionRef.current = performSwitch;
        setShowAbortDialog(true);
        return;
      }

      performSwitch();
    },
    [activeSessionId, resetStreamingState, navigate, isStreamingActive]
  );

  // Auto-save active session when messages change (skip while streaming)
  useEffect(() => {
    if (!activeSessionId || messages.length === 0) return;
    // Don't persist mid-stream — wait until streaming finishes
    if (messages.some((m) => m.streaming)) return;
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === activeSessionId);
      if (idx === -1) return prev;
      if (prev[idx].messages === messages && prev[idx].tableChartsState === tableChartsState) return prev;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], messages, tableChartsState, updatedAt: Date.now() };
      return updated;
    });
  }, [messages, tableChartsState, activeSessionId]);

  // On mount: if URL has a sessionId, load that session
  useEffect(() => {
    if (!urlSessionId) return;
    const target = sessionsRef.current.find((s) => s.id === urlSessionId);
    if (target) {
      const cleanMessages = (target.messages || []).map((m) =>
        m.streaming ? { ...m, streaming: false } : m
      );
      setMessages(cleanMessages);
      setTableChartsState(target.tableChartsState || {});
      setSessionId(target.sessionId);
      setActiveSessionId(urlSessionId);
    } else {
      // No matching session found — redirect to home
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist sessions to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("dsense_chat_sessions", JSON.stringify(sessions.slice(0, MAX_SESSIONS)));
    } catch {
      /* ignore */
    }
  }, [sessions, MAX_SESSIONS]);

  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        cancelAnimationFrame(typingIntervalRef.current);
      }
    };
  }, []);

  // Set favicon to Dsense logo
  useEffect(() => {
    const favicon = document.querySelector("link[rel='icon']");
    if (favicon) {
      favicon.href = DsenseLogoUrl;
    }
  }, []);


  // Image lightbox: capture clicks on images inside markdown and open overlay
  const handleMarkdownClick = (event) => {
    const target = event.target;
    if (!target || target.tagName !== "IMG") return;

    const src = target.getAttribute("src");
    const alt = target.getAttribute("alt") || "";
    if (!src) return;

    setLightboxImage({ src, alt });
  };

  const extractAllMarkdownTables = (text) => {
    if (!text) return [];
    const lines = text.split("\n");
    const tables = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Potential table header row (support with or without leading '|')
      if (line.includes("|")) {
        const headerIndex = i;
        const separatorIndex = i + 1;

        if (
          separatorIndex < lines.length &&
          (() => {
            const sep = lines[separatorIndex].trim();
            // Looser check: separator row must contain at least one '|' and some '-'
            return sep.includes("|") && /-+/.test(sep.replace(/\s+/g, ""));
          })()
        ) {
          // We have a table header + separator, now collect body rows
          let end = separatorIndex;
          for (let j = separatorIndex + 1; j < lines.length; j++) {
            const bodyLine = lines[j];
            if (!bodyLine.includes("|")) {
              end = j - 1;
              break;
            }
            end = j;
          }

          const tableBlock = lines.slice(headerIndex, end + 1).join("\n");
          tables.push(tableBlock);

          i = end + 1;
          continue;
        }
      }

      i += 1;
    }

    return tables;
  };

  const markdownTableToCsv = (tableMarkdown) => {
    const lines = tableMarkdown
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length < 2) return "";

    const rows = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (i === 1 && /-+/.test(line.replace(/\|/g, ""))) {
        continue;
      }

      const cells = line
        .split("|")
        .map((c) => c.trim())
        .filter((c) => c.length > 0);

      if (cells.length === 0) continue;
      rows.push(cells);
    }

    const escapeCell = (cell) => {
      const needsQuotes = /[",\n]/.test(cell);
      let value = cell.replace(/"/g, '""');
      return needsQuotes ? `"${value}"` : value;
    };

    return rows.map((r) => r.map(escapeCell).join(",")).join("\n");
  };

  const downloadCsv = (filename, csvContent) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ---- CSV row parser (handles quoted fields) ----
  const parseCsvRow = (line) => {
    const cells = [];
    let current = "";
    let inQuotes = false;
    for (let c = 0; c < line.length; c++) {
      const ch = line[c];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === "," && !inQuotes) {
        cells.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
    cells.push(current.trim());
    return cells;
  };

  // ---- Convert a block of CSV lines into a markdown table ----
  const csvToMarkdownTable = (csv) => {
    if (!csv) return null;

    // Strip common leading whitespace (handles 4-space-indented blocks)
    const rawLines = csv.split("\n").filter((l) => l.trim().length > 0);
    if (rawLines.length < 1) return null;
    const leadingSpaces = rawLines.map((l) => l.match(/^(\s*)/)[1].length);
    const minIndent = Math.min(...leadingSpaces);
    const lines = minIndent > 0
      ? rawLines.map((l) => l.slice(minIndent))
      : rawLines;

    const rows = lines.map(parseCsvRow);

    // Use the most common (mode) column count for robustness
    const freq = {};
    rows.forEach((r) => { freq[r.length] = (freq[r.length] || 0) + 1; });
    const colCount = Number(
      Object.entries(freq).sort((a, b) => b[1] - a[1])[0][0]
    );
    if (colCount < 2) return null;

    // Accept if at least 60 % of rows are within ±3 columns of the mode
    const closeEnough = rows.filter((r) => Math.abs(r.length - colCount) <= 3).length;
    if (closeEnough < rows.length * 0.6) return null;

    // Normalize every row to colCount (pad short, trim long)
    const normalizedRows = rows.map((r) => {
      while (r.length < colCount) r.push("");
      return r.slice(0, colCount);
    });

    // Heuristic: first row is a header if it has at least one non-numeric cell
    const firstRow = normalizedRows[0];
    const looksLikeHeader = firstRow.some(
      (cell) => cell.length > 0 && Number.isNaN(Number(cell.replace(/[%$,]/g, "")))
    );

    const formatRow = (cells) => "| " + cells.join(" | ") + " |";

    if (looksLikeHeader) {
      const header = normalizedRows[0];
      const separator = header.map(() => "---");
      const body = normalizedRows.slice(1);
      return [formatRow(header), formatRow(separator), ...body.map(formatRow)].join("\n");
    } else {
      // No header — generate Col_1 .. Col_N
      const header = normalizedRows[0].map((_, i) => `Col_${i + 1}`);
      const separator = header.map(() => "---");
      return [formatRow(header), formatRow(separator), ...normalizedRows.map(formatRow)].join("\n");
    }
  };

  // ---- Convert HTML <table> to markdown table ----
  const htmlTableToMarkdownTable = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const table = doc.querySelector("table");
      if (!table) return null;

      const rows = [];
      table.querySelectorAll("tr").forEach((tr) => {
        const cells = [];
        tr.querySelectorAll("th, td").forEach((cell) => {
          cells.push(cell.textContent.trim().replace(/\|/g, "\\|"));
        });
        if (cells.length > 0) rows.push(cells);
      });
      if (rows.length < 1) return null;

      const colCount = Math.max(...rows.map((r) => r.length));
      const normalizedRows = rows.map((r) => {
        while (r.length < colCount) r.push("");
        return r;
      });

      const formatRow = (cells) => "| " + cells.join(" | ") + " |";

      // If first row came from <th> elements, treat as header
      const firstTr = table.querySelector("tr");
      const hasThHeader = firstTr && firstTr.querySelector("th");

      if (hasThHeader) {
        const header = normalizedRows[0];
        const separator = header.map(() => "---");
        return [formatRow(header), formatRow(separator), ...normalizedRows.slice(1).map(formatRow)].join("\n");
      }
      // No <th> — generate generic headers
      const header = normalizedRows[0].map((_, i) => `Col_${i + 1}`);
      const separator = header.map(() => "---");
      return [formatRow(header), formatRow(separator), ...normalizedRows.map(formatRow)].join("\n");
    } catch {
      return null;
    }
  };

  // ---- Convert XML table-like structures to markdown table ----
  const xmlTableToMarkdownTable = (xml) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, "text/xml");
      if (doc.querySelector("parsererror")) return null;

      const rowElements = doc.querySelectorAll("row, record, item, tr, entry");
      if (rowElements.length < 1) return null;

      const rows = [];
      rowElements.forEach((row) => {
        const cells = [];
        for (const child of row.children) {
          cells.push(child.textContent.trim().replace(/\|/g, "\\|"));
        }
        if (cells.length > 0) rows.push(cells);
      });
      if (rows.length < 1) return null;

      const colCount = Math.max(...rows.map((r) => r.length));
      const normalizedRows = rows.map((r) => {
        while (r.length < colCount) r.push("");
        return r;
      });

      // Use child tag names from first row as headers
      const firstRowEl = rowElements[0];
      const tagNames = Array.from(firstRowEl.children).map((c) => c.tagName);
      const header = tagNames.length === colCount ? tagNames : normalizedRows[0].map((_, i) => `Col_${i + 1}`);
      const separator = header.map(() => "---");

      const formatRow = (cells) => "| " + cells.join(" | ") + " |";
      return [formatRow(header), formatRow(separator), ...normalizedRows.map(formatRow)].join("\n");
    } catch {
      return null;
    }
  };

  // ---- Try to convert any content string to a markdown table ----
  const tryConvertToTable = (content, langHint) => {
    const trimmed = (content || "").trim();
    if (!trimmed) return null;

    // Already a markdown table — leave as-is
    if (trimmed.includes("|") && trimmed.includes("---")) return null;

    // HTML table
    if (/<table[\s>]/i.test(trimmed)) {
      const converted = htmlTableToMarkdownTable(trimmed);
      if (converted) return converted;
    }

    // XML tabular structure
    if (trimmed.startsWith("<") && trimmed.includes("</")) {
      const converted = xmlTableToMarkdownTable(trimmed);
      if (converted) return converted;
    }

    // CSV-like: lines with commas
    const csvLines = trimmed.split("\n").filter((l) => l.trim().length > 0);
    if (csvLines.length >= 1) {
      const commas = (csvLines[0].match(/,/g) || []).length;
      if (commas >= 2) {
        const converted = csvToMarkdownTable(trimmed);
        if (converted) return converted;
      }
    }

    return null;
  };

  // ---- Master converter: detects all table formats and converts to markdown ----
  const convertAllTableFormats = (text) => {
    if (!text) return text;

    let result = text;

    // 1) Inline HTML tables (not inside code fences): <table>...</table>
    result = result.replace(/<table[\s\S]*?<\/table>/gi, (match) => {
      const converted = htmlTableToMarkdownTable(match);
      return converted ? "\n" + converted + "\n" : match;
    });

    // 2) All fenced code blocks — try to convert content regardless of lang tag
    //    Handles ``` and ```` style fences, optional lang, \r\n, trailing spaces.
    result = result.replace(/`{3,}(\w*)[^\S\n]*\r?\n([\s\S]*?)`{3,}/g, (match, lang, content) => {
      const converted = tryConvertToTable(content, lang);
      return converted ? "\n" + converted + "\n" : match;
    });

    // 3) Raw (unfenced) CSV blocks: consecutive lines with same comma count (≥2), no pipes
    //    A line is CSV-like only if most "fields" are short (not natural language).
    const isCsvLine = (line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.includes("|") || trimmed.startsWith("<") || trimmed.startsWith("#") || trimmed.startsWith("```") || trimmed.startsWith("-") || trimmed.startsWith("*")) return false;
      const commas = (trimmed.match(/,/g) || []).length;
      if (commas < 2) return false;
      // Reject lines where average field length > 40 chars (likely prose)
      const fields = parseCsvRow(trimmed);
      const avgLen = fields.reduce((sum, f) => sum + f.length, 0) / fields.length;
      if (avgLen > 40) return false;
      return true;
    };

    const lines = result.split("\n");
    let i = 0;
    const segments = [];
    while (i < lines.length) {
      if (isCsvLine(lines[i])) {
        const start = i;
        const baseCommas = (lines[i].trim().match(/,/g) || []).length;
        while (i < lines.length && isCsvLine(lines[i]) && Math.abs((lines[i].trim().match(/,/g) || []).length - baseCommas) <= 3) {
          i++;
        }
        // Require at least 2 consecutive CSV lines to form a table
        if (i - start >= 2) {
          const csvBlock = lines.slice(start, i).join("\n");
          const converted = csvToMarkdownTable(csvBlock);
          if (converted) {
            segments.push(converted);
          } else {
            // Strip leading indent so marked doesn't render as a code block
            const blockLines = lines.slice(start, i);
            const indents = blockLines.map((l) => l.match(/^(\s*)/)[1].length);
            const minIn = Math.min(...indents);
            segments.push(...(minIn > 0 ? blockLines.map((l) => l.slice(minIn)) : blockLines));
          }
        } else {
          segments.push(...lines.slice(start, i));
        }
      } else {
        segments.push(lines[i]);
        i++;
      }
    }
    return segments.join("\n");
  };

  const splitTextByTables = (text, isStreaming) => {
    // During streaming, only extract native markdown tables — skip expensive
    // format conversions that can corrupt incomplete text mid-stream.
    const processed = isStreaming ? text : convertAllTableFormats(text);
    const tables = extractAllMarkdownTables(processed);
    if (!tables.length) {
      return [{ type: "text", content: processed }];
    }

    const sections = [];
    let cursor = 0;

    tables.forEach((tableMd, tIndex) => {
      const pos = processed.indexOf(tableMd, cursor);
      if (pos === -1) {
        return;
      }

      const before = processed.slice(cursor, pos);
      if (before) {
        sections.push({ type: "text", content: before });
      }

      sections.push({ type: "table", content: tableMd, tableIndex: tIndex });
      cursor = pos + tableMd.length;
    });

    const after = processed.slice(cursor);
    if (after) {
      sections.push({ type: "text", content: after });
    }

    return sections;
  };

  const sendMessageToBot = async (userMessage, historyQuestions) => {
    try {
      setIsLoading(true);
      lastFailedMessageRef.current = { userMessage, historyQuestions };

      const botMessageId = Date.now();
      const initialBotResponse = {
        id: botMessageId,
        data_type: "TEXT",
        text: "",
        pdf_files: [],
        sender: "bot",
        timestamp: new Date(),
        error: false,
        streaming: true,
      };
      setMessages((prev) => [...prev, initialBotResponse]);
      setIsLoading(false);

      currentBotIdRef.current = botMessageId;
      typingBufferRef.current = "";
      displayedTextRef.current = "";
      let pdfFiles = [];

      // AbortController with rolling timeout — resets on every chunk so
      // a hanging backend is detected even mid-stream.
      const controller = new AbortController();
      abortControllerRef.current = controller;
      let streamTimer = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);
      const resetStreamTimer = () => {
        clearTimeout(streamTimer);
        streamTimer = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);
      };

      const response = await fetch(`${API_ENDPOINT}/api/v1/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question: userMessage,
          history: historyQuestions,
          session_id: sessionId,
        }),
        signal: controller.signal,
      });

      resetStreamTimer(); // got initial response — reset

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Start rAF-based rendering loop — syncs with display refresh,
      // never stacks on slow devices unlike setInterval.
      if (!typingIntervalRef.current) {
        let lastFrameTime = 0;
        const FRAME_INTERVAL = 80; // ms between renders — ~12fps, smooth enough

        const renderFrame = (timestamp) => {
          if (!typingIntervalRef.current) return; // stopped

          if (timestamp - lastFrameTime >= FRAME_INTERVAL && typingBufferRef.current.length > 0) {
            lastFrameTime = timestamp;

            // Drain up to 5 complete lines or 80 chars per frame
            let revealed = "";
            let iterations = 0;
            while (typingBufferRef.current.length > 0 && iterations < 5) {
              const nlIndex = typingBufferRef.current.indexOf("\n");
              if (nlIndex !== -1) {
                revealed += typingBufferRef.current.slice(0, nlIndex + 1);
                typingBufferRef.current = typingBufferRef.current.slice(nlIndex + 1);
                iterations++;
              } else {
                const batchSize = Math.min(80, typingBufferRef.current.length);
                revealed += typingBufferRef.current.slice(0, batchSize);
                typingBufferRef.current = typingBufferRef.current.slice(batchSize);
                break;
              }
            }

            if (revealed) {
              displayedTextRef.current += revealed;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === currentBotIdRef.current
                    ? { ...msg, text: displayedTextRef.current }
                    : msg
                )
              );
              scrollToBottom();
            }
          }

          typingIntervalRef.current = requestAnimationFrame(renderFrame);
        };

        typingIntervalRef.current = requestAnimationFrame(renderFrame);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        resetStreamTimer(); // got a chunk — reset timeout

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split("\n");
        sseBuffer = lines.pop() || "";

        let metadataUpdate = null;

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) continue;

          const jsonStr = trimmed.slice(6).trim();
          if (!jsonStr || jsonStr === "[DONE]") continue;

          try {
            const data = JSON.parse(jsonStr);

            if (data.type === "content" && data.data?.content) {
              typingBufferRef.current += data.data.content;
            } else if (data.type === "complete" && data.data) {
              if (data.data.pdf_files) {
                pdfFiles = data.data.pdf_files;
                metadataUpdate = { pdf_files: pdfFiles };
              }
            } else if (data.type === "metadata" && data.data?.pdf_files) {
              pdfFiles = data.data.pdf_files;
              metadataUpdate = { pdf_files: pdfFiles };
            } else if (data.answer) {
              typingBufferRef.current += data.answer;
              if (data.pdf_files) {
                pdfFiles = data.pdf_files;
                metadataUpdate = { pdf_files: pdfFiles };
              }
            }
          } catch {
            continue;
          }
        }

        if (metadataUpdate) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botMessageId ? { ...msg, ...metadataUpdate } : msg
            )
          );
        }
      }

      clearTimeout(streamTimer);

      // Process remaining SSE buffer
      if (sseBuffer.trim()) {
        const trimmed = sseBuffer.trim();
        if (trimmed.startsWith("data: ")) {
          const jsonStr = trimmed.slice(6).trim();
          if (jsonStr && jsonStr !== "[DONE]") {
            try {
              const data = JSON.parse(jsonStr);
              if (data.type === "content" && data.data?.content) {
                typingBufferRef.current += data.data.content;
              } else if (data.answer) {
                typingBufferRef.current += data.answer;
              }
            } catch {
              /* ignore */
            }
          }
        }
      }

      // Wait for typing buffer to fully flush, then mark complete
      const waitForFlush = () => {
        if (typingBufferRef.current.length > 0) {
          setTimeout(waitForFlush, 50);
          return;
        }
        if (typingIntervalRef.current) {
          cancelAnimationFrame(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMessageId
              ? {
                  ...msg,
                  text: displayedTextRef.current || msg.text,
                  streaming: false,
                  ...(pdfFiles.length ? { pdf_files: pdfFiles } : {}),
                }
              : msg
          )
        );
        currentBotIdRef.current = null;
        lastFailedMessageRef.current = null;
        abortControllerRef.current = null;
        scrollToBottom(true);
      };
      waitForFlush();
    } catch (error) {
      // If the abort was user-initiated (e.g. via the abort dialog), skip
      // error handling entirely — the caller already cleaned up state.
      if (error.name === "AbortError" && !abortControllerRef.current) {
        return;
      }

      console.error("Streaming error:", error);

      if (typingIntervalRef.current) {
        cancelAnimationFrame(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }

      // If we already received partial content, preserve it and mark complete
      // instead of replacing the message with an error.
      const partialText = displayedTextRef.current + typingBufferRef.current;
      if (partialText.length > 0) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === currentBotIdRef.current || (msg.streaming && msg.sender === "bot")
              ? {
                  ...msg,
                  text: partialText,
                  streaming: false,
                }
              : msg
          )
        );
      } else {
        // No content received at all — show retry
        setMessages((prev) => {
          const filtered = prev.filter((msg) => !msg.streaming);
          return [
            ...filtered,
            {
              data_type: "TEXT",
              text: "Please retry again, sorry for the inconvenience.",
              sender: "bot",
              timestamp: new Date(),
              error: true,
              retryable: true,
            },
          ];
        });
      }

      setIsLoading(false);
      currentBotIdRef.current = null;
      abortControllerRef.current = null;
    }
  };

  const handleRetry = () => {
    if (!lastFailedMessageRef.current) return;
    const { userMessage, historyQuestions } = lastFailedMessageRef.current;
    // Remove the retryable error message
    setMessages((prev) => prev.filter((msg) => !msg.retryable));
    sendMessageToBot(userMessage, historyQuestions);
  };

  const handleSuggestionClick = useCallback(async (suggestionText) => {
    if (!suggestionText || isLoading || messagesRef.current.some((m) => m.streaming)) return;
    setSidebarOpen(false);

    const userMessage = {
      data_type: "TEXT",
      text: suggestionText,
      sender: "user",
      timestamp: new Date(),
      error: false,
    };

    setMessages((prev) => [...prev, userMessage]);

    if (!activeSessionId) {
      const newId = generateId();
      const title =
        suggestionText.length > 50
          ? suggestionText.slice(0, 50) + "..."
          : suggestionText;
      setSessions((prev) =>
        [
          {
            id: newId,
            title,
            sessionId,
            messages: [userMessage],
            tableChartsState: {},
            updatedAt: Date.now(),
          },
          ...prev,
        ].slice(0, MAX_SESSIONS)
      );
      setActiveSessionId(newId);
      navigate(`/${newId}`, { replace: true });
    }

    const prevUserQuestions = messagesRef.current
      .filter((m) => m && m.sender === "user" && typeof m.text === "string")
      .map((m) => m.text.trim())
      .filter((t) => t.length > 0);

    const historyLimit = Number.isFinite(MAX_HISTORY_QUESTIONS)
      ? Math.max(0, MAX_HISTORY_QUESTIONS)
      : 5;
    const historyQuestions = prevUserQuestions.slice(-historyLimit);

    setInput("");
    setTimeout(scrollToBottom, 100);

    await sendMessageToBot(suggestionText, historyQuestions);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, activeSessionId, sessionId, navigate, scrollToBottom, MAX_HISTORY_QUESTIONS, MAX_SESSIONS]);

  const handleSendMessage = async () => {
    if (!input.trim() || input.trim().length <= 6 || isAnyStreaming) return;
    setSidebarOpen(false);

    const userMessage = {
      data_type: "TEXT",
      text: input.trim(),
      sender: "user",
      timestamp: new Date(),
      error: false,
    };

    setMessages((prev) => [...prev, userMessage]);

    // Create a session entry on the first message of a new chat
    const messageToSend = input.trim();
    if (!activeSessionId) {
      const newId = generateId();
      const title =
        messageToSend.length > 50
          ? messageToSend.slice(0, 50) + "..."
          : messageToSend;
      setSessions((prev) =>
        [
          {
            id: newId,
            title,
            sessionId,
            messages: [userMessage],
            tableChartsState: {},
            updatedAt: Date.now(),
          },
          ...prev,
        ].slice(0, MAX_SESSIONS)
      );
      setActiveSessionId(newId);
      navigate(`/${newId}`, { replace: true });
    }

    const prevUserQuestions = messages
      .filter((m) => m && m.sender === "user" && typeof m.text === "string")
      .map((m) => m.text.trim())
      .filter((t) => t.length > 0);

    const historyLimit = Number.isFinite(MAX_HISTORY_QUESTIONS)
      ? Math.max(0, MAX_HISTORY_QUESTIONS)
      : 5;
    const historyQuestions = prevUserQuestions.slice(-historyLimit);

    setInput("");

    // Scroll to bottom after user message
    setTimeout(scrollToBottom, 100);

    await sendMessageToBot(messageToSend, historyQuestions);
  };


  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const chat_result = (msg, index) => {
    if (msg.sender === "first") {
      return (
        <div
          style={{ fontSize: "0.9375rem", lineHeight: 1.6, color: "#374151" }}
          dangerouslySetInnerHTML={renderMarkdown(transformPathsToLinks(String(msg.text || "")))}
          onClick={handleMarkdownClick}
          className="markdown-content"
        />
      );
    } else if (msg.data_type === "TABLE") {
      return (
        <div style={{ overflowX: "auto", width: "100%" }}>
          <div style={{ fontSize: "0.9375rem", color: "#374151" }}>
            Table data: {JSON.stringify(msg.text)}
          </div>
        </div>
      );
    } else if (msg.data_type === "TEXT") {
      // Extract follow-up suggestions from the end of completed messages
      const isComplete = !msg.streaming && !msg.error;
      const { cleanText, suggestions } = isComplete
        ? extractSuggestions(msg.text || "")
        : { cleanText: msg.text || "", suggestions: [] };

      const hasVisibleText = !!(msg.text && msg.text.trim().length > 0);

      // While streaming and no text has arrived yet, the parent message
      // wrapper renders the ThinkingStages pill directly underneath the bot
      // avatar — chat_result intentionally renders nothing for that case.
      if (msg.streaming && !hasVisibleText && !msg.error) {
        return null;
      }

      const sections = splitTextByTables(cleanText, msg.streaming);

      return (
        <div>
          {!msg.error ? (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {isComplete && (
                  <VisualSummary markdown={cleanText} />
                )}
                {sections.map((section, sIndex) => {
                  if (section.type === "text") {
                    return (
                      <div
                        key={`text-${sIndex}`}
                        style={{
                          fontSize: "0.9375rem",
                          overflowX: "auto",
                          width: "100%",
                          textAlign: "left",
                        }}
                        dangerouslySetInnerHTML={renderMarkdown(
                          transformPathsToLinks(section.content || "")
                        )}
                        onClick={handleMarkdownClick}
                        className="markdown-content"
                      />
                    );
                  }

                  const tableChartConfig = buildTableChartConfig(section.content);
                  const hasChartData = !!tableChartConfig;

                  if (msg.streaming) {
                    return (
                      <div key={`table-${sIndex}`} style={{ marginTop: 4 }}>
                        <ExpandableTable
                          content={section.content}
                          onClick={handleMarkdownClick}
                        />
                      </div>
                    );
                  }

                  const stableMessageKey = msg.id || msg.timestamp || index;
                  const chartKey = `${stableMessageKey}-${sIndex}`;
                  const defaultType = tableChartConfig?.bestType || "bar";
                  // Auto-open the chart for any chartable table so users see
                  // both the raw table and the visualization by default.
                  const chartState = tableChartsState[chartKey] || {
                    open: hasChartData,
                    type: defaultType,
                  };

                  return (
                    <div key={`table-${sIndex}`} style={{ marginTop: 4 }}>
                      <ExpandableTable
                        content={section.content}
                        onClick={handleMarkdownClick}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box
                          component="button"
                          onClick={() => {
                            const csv = markdownTableToCsv(section.content);
                            if (!csv) return;
                            downloadCsv(
                              `table-${index + 1}-${(section.tableIndex || 0) + 1}.csv`,
                              csv
                            );
                          }}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "8px",
                            border: "1px solid #e5e7eb",
                            backgroundColor: "#ffffff",
                            cursor: "pointer",
                            fontSize: "0.8125rem",
                            color: "#6b7280",
                            fontFamily: "inherit",
                            transition: "all 0.15s ease",
                            "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" },
                            "&:focus": { outline: "none" },
                            "&:active": { backgroundColor: "#ffffff" },
                          }}
                        >
                          <DownloadIcon sx={{ fontSize: 14 }} />
                          Download CSV
                        </Box>
                        {hasChartData && (
                          <Box
                            component="button"
                            onClick={() => {
                              setTableChartsState((prev) => {
                                const prevState = prev[chartKey] || {
                                  open: false,
                                  type: defaultType,
                                };
                                return {
                                  ...prev,
                                  [chartKey]: {
                                    ...prevState,
                                    open: !prevState.open,
                                  },
                                };
                              });
                            }}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.5,
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: chartState.open ? "#c4b5fd" : "#e5e7eb",
                              backgroundColor: chartState.open ? "#f5f3ff" : "#ffffff",
                              cursor: "pointer",
                              fontSize: "0.8125rem",
                              color: chartState.open ? "#7c3aed" : "#6b7280",
                              fontFamily: "inherit",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                backgroundColor: chartState.open ? "#ede9fe" : "#f9fafb",
                                borderColor: chartState.open ? "#a78bfa" : "#d1d5db",
                              },
                            }}
                          >
                            {chartState.open ? "Hide Chart" : "Create Chart"}
                          </Box>
                        )}
                      </Box>

                      {hasChartData && chartState.open && (
                        <Box
                          sx={{
                            mt: 1.5,
                            p: 1.5,
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            backgroundColor: "#fafafa",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mb: 1,
                            }}
                          >
                            {(tableChartConfig?.availableTypes || [
                              "bar",
                              "line",
                              "area",
                              "stackedBar",
                              "pie",
                            ]).map((t) => (
                              <Box
                                component="button"
                                key={t}
                                onClick={() =>
                                  setTableChartsState((prev) => {
                                    const prevState = prev[chartKey] || {
                                      open: false,
                                      type: defaultType,
                                    };
                                    return {
                                      ...prev,
                                      [chartKey]: {
                                        ...prevState,
                                        type: t,
                                      },
                                    };
                                  })
                                }
                                sx={{
                                  px: 1.25,
                                  py: 0.25,
                                  borderRadius: "6px",
                                  border: "1px solid",
                                  borderColor:
                                    chartState.type === t ? "#c4b5fd" : "#e5e7eb",
                                  backgroundColor:
                                    chartState.type === t ? "#f5f3ff" : "#ffffff",
                                  color:
                                    chartState.type === t ? "#7c3aed" : "#6b7280",
                                  cursor: "pointer",
                                  fontSize: "0.75rem",
                                  fontFamily: "inherit",
                                  transition: "all 0.15s ease",
                                  "&:hover": {
                                    backgroundColor:
                                      chartState.type === t ? "#ede9fe" : "#f4f4f5",
                                  },
                                }}
                              >
                                {CHART_TYPE_LABELS[t] || t}
                              </Box>
                            ))}
                          </Box>
                          <TableChart
                            tableMarkdown={section.content}
                            chartType={chartState.type}
                          />
                        </Box>
                      )}
                    </div>
                  );
                })}

                {msg.streaming && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          backgroundColor: "#9d6fff",
                          animation: `streamingDot 1.4s infinite ease-in-out both`,
                          animationDelay: `${i * 0.16}s`,
                          "@keyframes streamingDot": {
                            "0%, 80%, 100%": { transform: "scale(0)" },
                            "40%": { transform: "scale(1)" },
                          },
                        }}
                      />
                    ))}
                  </Box>
                )}

                {suggestions.length > 0 && !msg.streaming && (
                  <Box sx={{ mt: 0, display: "flex", flexDirection: "column", gap: 1, textAlign: "left" }}>
                    <div
                      className="markdown-content"
                      style={{ fontSize: "0.9375rem", textAlign: "left" }}
                    >
                      <h3 style={{ marginTop: 0, marginBottom: 6, color: "#1a1a2e", fontWeight: 600, fontSize: "1rem" }}>
                        What would you like to explore next?
                      </h3>
                    </div>
                    {suggestions.map((s, si) => (
                      <Box
                        key={si}
                        component="button"
                        onClick={() => handleSuggestionClick(s)}
                        sx={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          px: 2,
                          py: 1.25,
                          borderRadius: "10px",
                          border: "1px solid #e5e7eb",
                          backgroundColor: "#ffffff",
                          cursor: "pointer",
                          fontSize: "0.8125rem",
                          lineHeight: 1.5,
                          color: "#374151",
                          fontFamily: "inherit",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            backgroundColor: "#f5f3ff",
                            borderColor: "#c4b5fd",
                            color: "#5b21b6",
                          },
                        }}
                      >
                        {s}
                      </Box>
                    ))}
                  </Box>
                )}
              </div>
            </div>
          ) : (
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  fontSize: "0.9375rem",
                  color: "#dc2626",
                  mb: msg.retryable ? 1.5 : 0,
                }}
                dangerouslySetInnerHTML={renderMarkdown(
                  transformPathsToLinks(String(msg.text || ""))
                )}
                onClick={handleMarkdownClick}
                className="markdown-content error-message"
              />
              {msg.retryable && (
                <Box
                  component="button"
                  onClick={handleRetry}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 2,
                    py: 0.75,
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                    backgroundColor: "#ffffff",
                    color: "#374151",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#f4f4f5",
                      borderColor: "#d1d5db",
                    },
                  }}
                >
                  ↻ Retry
                </Box>
              )}
            </Box>
          )}
        </div>
      );
    }

    return null;
  };

  const isAnyStreaming = messages.some((m) => m.streaming);
  const canSend = input.trim() && input.trim().length > 6 && !isLoading && !isAnyStreaming;

  return (
    <div
      id="dview-chatbot"
      style={{ height: "100dvh", minHeight: "100vh", display: "flex", backgroundColor: "#ffffff", overflow: "hidden" }}
    >
      <style>{markdownStyles}</style>

      {/* ===== Sidebar ===== */}
      <Box
        sx={{
          width: sidebarOpen ? 260 : 0,
          flexShrink: 0,
          backgroundColor: "#f9f9f9",
          borderRight: sidebarOpen ? "1px solid #ececec" : "none",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease, transform 0.25s ease",
          overflow: "hidden",
          "@media (max-width: 768px)": {
            position: "fixed",
            top: 0,
            left: 0,
            width: sidebarOpen ? "min(260px, 80vw)" : 0,
            zIndex: 1200,
            height: "100dvh",
            boxShadow: sidebarOpen ? "4px 0 12px rgba(0,0,0,0.15)" : "none",
          },
        }}
      >
        {/* New Chat button */}
        <Box sx={{ p: 1.5 }}>
          <Box
            component="button"
            onClick={handleNewChat}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
              backgroundColor: "#ffffff",
              cursor: "pointer",
              fontSize: "0.875rem",
              color: "#374151",
              fontFamily: "inherit",
              fontWeight: 500,
              width: "100%",
              transition: "all 0.15s ease",
              "&:hover": { backgroundColor: "#f4f4f5" },
            }}
          >
            <AddIcon sx={{ fontSize: 18 }} />
            New Chat
          </Box>
        </Box>

        {/* Session list */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 1, pb: 2 }}>
          {sessions.slice(0, MAX_SESSIONS).map((session) => (
            <Box
              key={session.id}
              onClick={() => switchSession(session.id)}
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: "8px",
                cursor: "pointer",
                backgroundColor:
                  session.id === activeSessionId ? "#ececec" : "transparent",
                "&:hover": {
                  backgroundColor:
                    session.id === activeSessionId ? "#e4e4e4" : "#f0f0f0",
                  "& .session-delete": { opacity: 1 },
                },
                mb: 0.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <ChatBubbleOutlineIcon
                sx={{ fontSize: 15, color: "#9ca3af", flexShrink: 0 }}
              />
              <Typography
                noWrap
                sx={{ fontSize: "0.8125rem", color: "#374151", flex: 1, minWidth: 0 }}
              >
                {session.title || "New Chat"}
              </Typography>
              <IconButton
                className="session-delete"
                size="small"
                onClick={(e) => deleteSession(session.id, e)}
                sx={{
                  opacity: 0,
                  p: 0.25,
                  color: "#9ca3af",
                  flexShrink: 0,
                  transition: "opacity 0.15s, color 0.15s",
                  "@media (hover: none)": {
                    opacity: 0.7,
                  },
                  "&:hover": { color: "#ef4444", backgroundColor: "transparent" },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            display: "block",
            position: "fixed",
            inset: 0,
            zIndex: 1199,
            backgroundColor: "rgba(0,0,0,0.3)",
            "@media (min-width: 769px)": {
              display: "none",
            },
          }}
        />
      )}

      {/* ===== Main content ===== */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            px: 2,
            py: 1.25,
            borderBottom: "none",
            backgroundColor: "#ffffff",
          }}
        >
          <IconButton
            size="small"
            onClick={() => setSidebarOpen((prev) => !prev)}
            sx={{
              color: "#6b7280",
              borderRadius: "8px",
              p: 0.75,
              "&:hover": { backgroundColor: "#f4f4f5" },
            }}
          >
            <MenuIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Chat area */}
        <ChatContainer ref={chatContainerRef} onClick={() => sidebarOpen && setSidebarOpen(false)}>
          <Box
            sx={{
              maxWidth: "768px",
              width: "100%",
              mx: "auto",
              px: { xs: 2, sm: 3 },
              py: 3,
            }}
          >
            {/* Empty state welcome */}
            {messages.length === 0 && !isLoading && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "60vh",
                  gap: 1.5,
                }}
              >
                <PrismLogoIcon size={88} />
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    color: "#351049",
                    lineHeight: 1,
                  }}
                >
                  PRISM AI
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.9375rem", sm: "1.0625rem" },
                    fontWeight: 500,
                    color: "#6b7280",
                  }}
                >
                  30,000 reports. 1.2 million data points. One conversation
                </Typography>
              </Box>
            )}

            {messages.map((msg, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                {msg.sender === "user" ? (
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <MessageBubble>
                      <Typography sx={{ fontSize: "0.9375rem" }}>
                        {msg.text}
                      </Typography>
                    </MessageBubble>
                  </Box>
                ) : (() => {
                  const isThinking =
                    msg.sender === "bot" &&
                    msg.streaming &&
                    !msg.error &&
                    !(msg.text && msg.text.trim().length > 0);

                  // Avatar block — identical in both states so the logo's
                  // top-left position is pixel-stable across the
                  // thinking → content transition.
                  const avatar = (
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <PrismLogoIcon size={42} />
                    </Box>
                  );

                  // While "thinking": row — avatar on the left, status to
                  // its right, vertically centred with the avatar. The
                  // avatar sits at (0, 0) of the message container so it
                  // never visually shifts when content arrives.
                  if (isThinking) {
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                          width: "100%",
                        }}
                      >
                        {avatar}
                        <Box
                          sx={{
                            minHeight: 42,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <ThinkingStages hasContent={false} />
                        </Box>
                      </Box>
                    );
                  }

                  // Once content arrives: row — avatar on the left, response
                  // to its right. The avatar still sits at (0, 0) of the
                  // message container, so it doesn't visually shift; only
                  // the layout of what surrounds it changes.
                  const showActions = !msg.streaming && !msg.error && !!msg.text;

                  return (
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                    >
                      {avatar}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {chat_result(msg, index)}
                        {showActions && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              mt: 1,
                            }}
                          >
                            <AnswerActions text={msg.text || ""} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })()}
              </Box>
            ))}

            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  alignItems: "flex-start",
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PrismLogoIcon size={42} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    py: 1,
                  }}
                >
                  {[0, 1, 2].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: "#9d6fff",
                        animation: `bounce 1.4s infinite ease-in-out both`,
                        animationDelay: `${i * 0.16}s`,
                        "@keyframes bounce": {
                          "0%, 80%, 100%": { transform: "scale(0)" },
                          "40%": { transform: "scale(1)" },
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <div
              ref={messagesEndRef}
              style={{ height: 0, overflow: "hidden" }}
            />
          </Box>
        </ChatContainer>

        {/* Image lightbox */}
        {lightboxImage && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0,0,0,0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
            onClick={() => setLightboxImage(null)}
          >
            <Box
              component="img"
              src={lightboxImage.src}
              alt={lightboxImage.alt}
              sx={{
                maxWidth: "90vw",
                maxHeight: "80vh",
                borderRadius: 2,
                boxShadow: 6,
                backgroundColor: "white",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            {lightboxImage.alt && (
              <Typography
                variant="caption"
                sx={{ mt: 1, color: "#f5f5f5", textAlign: "center" }}
              >
                {lightboxImage.alt}
              </Typography>
            )}
          </Box>
        )}

        {/* Input area */}
        <InputContainer>
          <Box sx={{ maxWidth: "768px", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                border: "1px solid #d9d9e3",
                borderRadius: "24px",
                backgroundColor: "#f4f4f4",
                px: 2,
                py: 0.5,
                transition: "border-color 0.2s, box-shadow 0.2s",
                "&:focus-within": {
                  borderColor: "#9d6fff",
                  boxShadow: "0 0 0 2px rgba(157,111,255,0.08)",
                },
              }}
            >
              <TextField
                fullWidth
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything..."
                variant="outlined"
                size="small"
                onKeyDown={handleKeyDown}
                multiline
                maxRows={4}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { border: "none" },
                    padding: "6px 0",
                    fontSize: "0.9375rem",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    opacity: 0.5,
                  },
                }}
              />
              <IconButton
                disabled={!canSend}
                onClick={handleSendMessage}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: canSend ? "#1a1a1a" : "transparent",
                  mb: 0.5,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: canSend ? "#333333" : "transparent",
                  },
                }}
              >
                <AnimatedSendIcon canSend={canSend} />
              </IconButton>
            </Box>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                textAlign: "center",
                mt: 1,
                color: "#9ca3af",
                fontSize: "0.75rem",
              }}
            >
              Prism AI Research Assistant may make mistakes. Check important info. &nbsp;|&nbsp; Source: Grand View Research
            </Typography>
          </Box>
        </InputContainer>
      </Box>
      {/* Abort confirmation dialog */}
      <Dialog
        open={showAbortDialog}
        onClose={handleAbortDialogContinue}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            px: 1,
            py: 0.5,
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem", pb: 0.5 }}>
          Question in progress
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.9rem", color: "#4b5563" }}>
            A question is still being answered. Do you want to continue waiting or abort the current question?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleAbortDialogContinue}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: "#374151",
              borderRadius: "10px",
            }}
          >
            Continue
          </Button>
          <Button
            onClick={handleAbortDialogAbort}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              backgroundColor: "#dc2626",
              "&:hover": { backgroundColor: "#b91c1c" },
            }}
          >
            Abort
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Homepage;
